import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useCallback } from 'react'

import type { ChecklistItem } from 'src/analytics'
import { JourneyScope } from 'src/analytics'
import {
  JourneyActionMethodEnum,
  JourneyActionStatusEnum,
  onboardingJourneysPartialUpdateMutation,
  onboardingJourneysRetrieveQueryKey,
  type OnboardingStepErrorCodeEnum,
  type OnboardingJourney
} from 'src/api/main'
import { useSessionContext } from 'src/hooks'

type ReportOptions = {
  /** Defaults to `done`; pass `failed` with an `errorCode`, or `todo` to reopen. */
  status?: JourneyActionStatusEnum
  errorCode?: OnboardingStepErrorCodeEnum
}

/**
 * Tells the platform a journey step is settled.
 *
 * The client is what knows: a step closes when the user actually does the
 * thing — sends the invitations, saves the settings — and the platform records
 * it and emits `onboarding_step_completed` from the `set_step` it receives.
 * Which is why this is reported from the screen that performed the action
 * rather than from the checklist: pressing a row there only opens a page, and
 * "opened it once" is not the same claim as "did it".
 *
 * The PATCH answers with the whole updated journey, so the reply is written
 * straight into the sidebar's cache — no refetch, and no optimistic guess a
 * later fetch could contradict.
 */
export const useReportJourneyStep = (workspaceId?: number) => {
  const queryClient = useQueryClient()
  const { activeWorkspaceId } = useSessionContext()

  // Account screens have no workspace of their own to name, so they fall back
  // to the one the session is on — which is the journey the member checklist
  // they are ticking belongs to.
  const organization = workspaceId ?? activeWorkspaceId

  const { mutateAsync: patchJourney } = useMutation(
    onboardingJourneysPartialUpdateMutation()
  )

  return useCallback(
    (step: ChecklistItem, { status, errorCode }: ReportOptions = {}) => {
      if (organization == null) return

      const request = {
        path: { scope: JourneyScope.CORE },
        query: { organization }
      }

      void patchJourney({
        ...request,
        body: {
          action: 'set_step',
          step_key: step,
          status: status ?? JourneyActionStatusEnum.DONE,
          method: JourneyActionMethodEnum.WIZARD,
          ...(errorCode ? { error_code: errorCode } : {})
        }
      })
        .then((next) => {
          queryClient.setQueryData<OnboardingJourney>(
            onboardingJourneysRetrieveQueryKey(request),
            next
          )
        })
        .catch(() => {
          // A step that could not be reported is a checklist that keeps asking,
          // which is the harmless direction. The action the user came for has
          // already succeeded, and failing it over the bookkeeping would be
          // the worse trade.
        })
    },
    [organization, patchJourney, queryClient]
  )
}
